import React from 'react';
import MDEditor from '@uiw/react-md-editor';
import * as commands from '@uiw/react-md-editor/commands';
import { cn } from '@/lib/utils';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: number;
  className?: string;
  preview?: 'edit' | 'preview' | 'live';
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Գրեք ձեր բովանդակությունը...',
  height = 300,
  className,
  preview = 'edit'
}) => {
  return (
    <div className={cn('w-full', className)}>
      <MDEditor
        value={value}
        onChange={(val) => onChange(val || '')}
        height={height}
        preview={preview}
        data-color-mode="dark"
        className="!bg-gray-900 !border-green-400/30"
        textareaProps={{
          placeholder,
          className: '!bg-gray-800 !text-white !border-green-400/30 !font-mono',
          style: {
            fontSize: 14,
            backgroundColor: '#1f2937',
            color: '#ffffff',
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", monospace'
          }
        }}
        toolbarHeight={50}
        previewOptions={{
          className: '!bg-gray-800 !text-white prose prose-invert max-w-none',
          style: {
            backgroundColor: '#1f2937',
            color: '#ffffff',
            padding: '16px'
          }
        }}
        commands={[
          commands.bold,
          commands.italic,
          commands.strikethrough,
          commands.hr,
          commands.group([
            commands.title1,
            commands.title2,
            commands.title3,
            commands.title4,
            commands.title5,
            commands.title6,
          ], {
            name: 'title',
            groupName: 'title',
            buttonProps: { 'aria-label': 'Insert title', title: 'Insert title' }
          }),
          commands.divider,
          commands.unorderedListCommand,
          commands.orderedListCommand,
          commands.checkedListCommand,
          commands.divider,
          commands.quote,
          commands.code,
          commands.codeBlock,
          commands.divider,
          commands.link,
          commands.image,
          commands.table,
          commands.divider,
          commands.fullscreen,
        ]}
      />
      
      {/* Custom styling for better dark theme integration */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .w-md-editor {
          background-color: #1f2937 !important;
          border: 1px solid rgba(34, 197, 94, 0.3) !important;
          border-radius: 0.375rem !important;
        }
        
        .w-md-editor-toolbar {
          background-color: #374151 !important;
          border-bottom: 1px solid rgba(34, 197, 94, 0.3) !important;
          padding: 8px !important;
        }
        
        .w-md-editor-toolbar-divider {
          background-color: rgba(34, 197, 94, 0.3) !important;
        }
        
        .w-md-editor-toolbar button {
          color: #10b981 !important;
          background-color: transparent !important;
          border: 1px solid transparent !important;
          border-radius: 0.25rem !important;
          padding: 4px 8px !important;
          margin: 0 2px !important;
          font-family: ui-monospace, SFMono-Regular, "SF Mono", monospace !important;
        }
        
        .w-md-editor-toolbar button:hover {
          background-color: rgba(34, 197, 94, 0.1) !important;
          border-color: rgba(34, 197, 94, 0.3) !important;
          color: #34d399 !important;
        }
        
        .w-md-editor-toolbar button.active {
          background-color: rgba(34, 197, 94, 0.2) !important;
          border-color: rgba(34, 197, 94, 0.5) !important;
          color: #ffffff !important;
        }
        
        .w-md-editor-text-textarea,
        .w-md-editor-text {
          background-color: #1f2937 !important;
          color: #ffffff !important;
          border: none !important;
          font-family: ui-monospace, SFMono-Regular, "SF Mono", monospace !important;
          font-size: 14px !important;
          line-height: 1.5 !important;
        }
        
        .w-md-editor-preview {
          background-color: #1f2937 !important;
          color: #ffffff !important;
          border-left: 1px solid rgba(34, 197, 94, 0.3) !important;
        }
        
        .w-md-editor-preview h1,
        .w-md-editor-preview h2,
        .w-md-editor-preview h3,
        .w-md-editor-preview h4,
        .w-md-editor-preview h5,
        .w-md-editor-preview h6 {
          color: #10b981 !important;
          border-bottom-color: rgba(34, 197, 94, 0.3) !important;
        }
        
        .w-md-editor-preview code {
          background-color: #374151 !important;
          color: #34d399 !important;
          padding: 2px 4px !important;
          border-radius: 3px !important;
        }
        
        .w-md-editor-preview pre {
          background-color: #111827 !important;
          border: 1px solid rgba(34, 197, 94, 0.3) !important;
          border-radius: 0.375rem !important;
        }
        
        .w-md-editor-preview pre code {
          background-color: transparent !important;
          color: #ffffff !important;
        }
        
        .w-md-editor-preview blockquote {
          border-left: 4px solid #10b981 !important;
          background-color: rgba(34, 197, 94, 0.05) !important;
          color: #d1fae5 !important;
        }
        
        .w-md-editor-preview table {
          border-collapse: collapse !important;
        }
        
        .w-md-editor-preview table th,
        .w-md-editor-preview table td {
          border: 1px solid rgba(34, 197, 94, 0.3) !important;
          padding: 8px 12px !important;
        }
        
        .w-md-editor-preview table th {
          background-color: rgba(34, 197, 94, 0.1) !important;
          color: #10b981 !important;
          font-weight: 600 !important;
        }
        
        .w-md-editor-preview a {
          color: #60a5fa !important;
          text-decoration: underline !important;
        }
        
        .w-md-editor-preview a:hover {
          color: #93c5fd !important;
        }
        
        .w-md-editor-preview img {
          max-width: 100% !important;
          height: auto !important;
          border-radius: 0.375rem !important;
          border: 1px solid rgba(34, 197, 94, 0.3) !important;
        }
        
        .w-md-editor-focus {
          border-color: rgba(34, 197, 94, 0.5) !important;
          box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.1) !important;
        }
        
        @media (max-width: 768px) {
          .w-md-editor-toolbar {
            flex-wrap: wrap !important;
            gap: 4px !important;
          }
          
          .w-md-editor-toolbar button {
            font-size: 12px !important;
            padding: 3px 6px !important;
          }
          
          .w-md-editor {
            font-size: 13px !important;
          }
          
          .w-md-editor-text-textarea,
          .w-md-editor-text {
            font-size: 13px !important;
          }
        }
        `
      }} />
    </div>
  );
};